import { type SyntheticEvent, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { TextField } from '@/components/form'
import { MutationError } from '@/components/mutation-error'
import { SectionCard } from '@/components/section-card'
import { SocialLinksSummary } from '@/components/social-links-summary'
import { countryName } from '@/utils/countries'
import { countryFlagEmoji } from '@/utils/country-flag'
import { isTempEmail } from '@/utils/is-temp-email'

import { SectionHeader } from '../section-header'
import { StackedField } from '../stacked-field'
import { usePartnerUpdate } from '../use-partner-update'

type PersonalInfoCardProps = {
  className?: string
  partner: Pick<
    AffiliateAdminPartnerDetailSchema,
    'display_name' | 'email' | 'country' | 'social_links'
  >
  partnerId: string
}

export const PersonalInfoCard = ({ className, partner, partnerId }: PersonalInfoCardProps) => {
  const { formatMessage } = useIntl()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ display_name: '', email: '', country: '' })
  const { mutation, hasError } = usePartnerUpdate(partnerId, () => setEditing(false))

  const startEditing = () => {
    setForm({ display_name: partner.display_name, email: partner.email, country: partner.country })
    setEditing(true)
    mutation.reset()
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    mutation.mutate({ partnerId, data: form })
  }

  return (
    <SectionCard
      className={className}
      onSubmit={editing ? handleSubmit : undefined}
    >
      <SectionHeader
        editing={editing}
        saving={mutation.isPending}
        title={<FormattedMessage id="profile.details" />}
        onCancel={() => setEditing(false)}
        onEdit={startEditing}
        onSave={() => undefined}
      />
      {editing ? (
        <div className="flex flex-col gap-3">
          <StackedField label={formatMessage({ id: 'profile.displayName' })}>
            <TextField
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            />
          </StackedField>
          <StackedField label={formatMessage({ id: 'profile.email' })}>
            <TextField
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </StackedField>
          <StackedField label={formatMessage({ id: 'profile.country' })}>
            <TextField
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            />
          </StackedField>
          <MutationError
            messageId="admin.partnerDetail.save.error"
            show={hasError}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <StackedField label={formatMessage({ id: 'profile.displayName' })}>
            <Typography variant="body2">{partner.display_name}</Typography>
          </StackedField>
          <StackedField label={formatMessage({ id: 'profile.email' })}>
            {isTempEmail(partner.email) ? (
              <Typography
                className="italic"
                color="text.secondary"
                variant="body2"
              >
                <FormattedMessage id="admin.partners.tempEmail.label" />
              </Typography>
            ) : (
              <Typography
                className="truncate font-mono"
                component="div"
                title={partner.email}
                variant="body2"
              >
                {partner.email}
              </Typography>
            )}
          </StackedField>
          <StackedField label={formatMessage({ id: 'profile.country' })}>
            <Typography variant="body2">
              {partner.country
                ? `${countryFlagEmoji(partner.country)} ${countryName(partner.country)}`
                : ''}
            </Typography>
          </StackedField>
          {/* Read-only here: admins view a partner's social links but do not
              edit them — the partner self-manages them on the settings page. */}
          <StackedField label={formatMessage({ id: 'profile.socialLinks' })}>
            <SocialLinksSummary links={partner.social_links} />
          </StackedField>
        </div>
      )}
    </SectionCard>
  )
}
